const TIME = "(?:\\d{1,2}:)?\\d{1,2}:\\d{2}(?:[.,]\\d{1,3})?";
const LABEL = "(?:Speaker|Locuteur|Intervenant|Orateur|Participant|Personne|Interlocuteur|Voix)\\s*\\d+|Th[ée]rapeute|Patient(?:e)?|Psy(?:chologue)?|[A-ZÀ-Ý][\\wÀ-ÿ'’.-]{1,24}(?:\\s[A-ZÀ-Ý][\\wÀ-ÿ'’.-]{1,24})?";

const RE = {
  srtTime: new RegExp(`^\\s*${TIME}\\s*-->\\s*${TIME}\\s*$`),
  index: /^\s*\d{1,4}\s*$/,
  // [00:00:03] Speaker 1: text  |  00:00:03 Speaker 1: text  |  (00:03) Speaker 1 : text
  timeLabel: new RegExp(`^\\s*[\\[(]?(${TIME})[\\])]?\\s*[-–]?\\s*(${LABEL})\\s*[:：]\\s*(.*)$`),
  // Speaker 1 [00:00:03]: text  |  Speaker 1 (00:03) text  |  Speaker 1 00:00:03
  labelTime: new RegExp(`^\\s*(${LABEL})\\s*[\\[(]?(${TIME})[\\])]?\\s*[:：]?\\s*(.*)$`),
  // Speaker 1: text
  labelOnly: new RegExp(`^\\s*(${LABEL})\\s*[:：]\\s*(.*)$`),
  // [00:00:03] text (no speaker)
  timeOnly: new RegExp(`^\\s*[\\[(]?(${TIME})[\\])]?\\s*[-–]?\\s*(.*)$`),
};

const toMmSs = (t) => {
  if (!t) return null;
  const parts = t.replace(",", ".").split(":").map(Number);
  let secs = 0;
  if (parts.length === 3) secs = parts[0] * 3600 + parts[1] * 60 + parts[2];
  else if (parts.length === 2) secs = parts[0] * 60 + parts[1];
  secs = Math.floor(secs);
  const m = Math.floor(secs / 60), s = secs % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const isKintsugiLabel = (l) => /^th[ée]rapeute$/i.test(l) || /^patiente?$/i.test(l);

/** Normalise une transcription (Plaud, Otter, SRT…) au format Kintsugi : "[mm:ss] Thérapeute : texte". */
export function normalizeTranscript(raw, { swap = false } = {}) {
  const lines = raw.replace(/\r/g, "").split("\n");
  const turns = [];
  let cur = null;
  let pendingTime = null;

  const push = (label, time, text) => {
    cur = { label, time, text: (text || "").trim() };
    turns.push(cur);
  };

  for (const line of lines) {
    if (!line.trim()) continue;
    if (RE.index.test(line)) continue;
    const srt = line.match(RE.srtTime);
    if (srt) { pendingTime = line.trim().split(/\s*-->\s*/)[0]; continue; }
    let m;
    if ((m = line.match(RE.timeLabel))) { push(m[2], m[1], m[3]); continue; }
    if ((m = line.match(RE.labelTime))) { push(m[1], m[2], m[3]); continue; }
    if ((m = line.match(RE.labelOnly)) && m[2].length < 2000) { push(m[1], pendingTime, m[2]); pendingTime = null; continue; }
    if ((m = line.match(RE.timeOnly))) {
      if (cur && !cur.text) { cur.time = cur.time || m[1]; cur.text = m[2].trim(); }
      else push(cur?.label ?? null, m[1], m[2]);
      continue;
    }
    if (cur) cur.text = cur.text ? `${cur.text} ${line.trim()}` : line.trim();
    else push(null, pendingTime, line.trim());
    pendingTime = null;
  }

  const labels = [];
  for (const t of turns) if (t.label && !labels.some((l) => l.toLowerCase() === t.label.toLowerCase())) labels.push(t.label);
  const mapping = {};
  const known = labels.filter(isKintsugiLabel);
  if (known.length) {
    for (const l of labels) mapping[l.toLowerCase()] = /^th/i.test(l) ? "Thérapeute" : /^pat/i.test(l) ? "Patient" : "Locuteur ?";
  } else {
    const [a, b] = swap ? [labels[1], labels[0]] : [labels[0], labels[1]];
    for (const l of labels) mapping[l.toLowerCase()] = l === a ? "Thérapeute" : l === b ? "Patient" : "Locuteur ?";
  }
  if (known.length && swap) {
    for (const k of Object.keys(mapping)) mapping[k] = mapping[k] === "Thérapeute" ? "Patient" : mapping[k] === "Patient" ? "Thérapeute" : mapping[k];
  }

  const merged = [];
  for (const t of turns) {
    const who = t.label ? mapping[t.label.toLowerCase()] : "Locuteur ?";
    const prev = merged[merged.length - 1];
    if (prev && prev.who === who && !t.time) { prev.text = `${prev.text} ${t.text}`.trim(); continue; }
    merged.push({ who, time: toMmSs(t.time), text: t.text });
  }

  const out = merged.filter((t) => t.text).map((t) => `${t.time ? `[${t.time}] ` : ""}${t.who} : ${t.text}`);
  return {
    text: out.join("\n"),
    turns: out.length,
    speakers: labels.map((l) => ({ from: l, to: mapping[l.toLowerCase()] })),
    hasTimestamps: merged.some((t) => t.time),
  };
}
